/**
 * Approvals (design G): every customer "I've paid" claim waiting for the
 * owner, oldest first, with the slip beside it. Decisions go to the server one
 * at a time (or approve-bulk) and the queue is fetched again after each, so
 * nothing on screen is decided locally.
 *
 * "Recently decided" lists decisions made in this browser session only: the
 * API has no decision history yet. It lives at module level so it survives
 * moving between screens, and is cleared by a reload.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import InvoiceRepository, { PendingClaim } from '../../../internals/repository/InvoiceRepository';
import { useSelectedShop } from '../useInvoicing';
import { METHOD_LABEL, date, money } from '../format';
import {
  APPROVER_ONLY, BulkSummary, balanceAfter, bulkSummary, claimErrorText, slipKind, totalOf, waiting,
} from '../approvalsModel';
import { DifferentAmountDialog, DuplicateTag, RejectDialog, STRIPES, SlipThumb } from '../components/ClaimPanel';
import { Dialog, ErrorBanner } from '../components/VoidModal';

interface Decision {
  paymentId: string;
  docId: string;
  number: string | null;
  customer: string;
  amountMinor: number;
  state: 'Approved' | 'Rejected';
  why: string;
  at: number;
}

const SESSION_DECISIONS: Record<string, Decision[]> = {};
const RECENT_MAX = 8;

const ZOOM_MIN = 0.6;
const ZOOM_MAX = 2.2;
const ZOOM_STEP = 0.2;

const GRID = '20px 38px minmax(200px,1.4fr) 120px 110px 100px 90px';
const capsCls = 'text-[11px] font-semibold uppercase tracking-[.07em] text-text3';

const custOf = (c: PendingClaim) => c.document?.customer?.companyName || c.document?.customer?.name || c.payerName || 'Customer';
const docLink = (docId: string) => `/invoices/${encodeURIComponent(docId)}`;

export default function ApprovalsView() {
  const shop = useSelectedShop();
  const shopId: string = shop?.shopId ?? '';
  const cur: string = shop?.currency || 'LKR';
  const [search, setSearch] = useSearchParams();

  const [items, setItems] = useState<PendingClaim[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<any>(null);
  const [selId, setSelId] = useState<string | null>(search.get('claim'));
  const [picked, setPicked] = useState<Record<string, boolean>>({});
  const [zoom, setZoom] = useState(1);
  const [rot, setRot] = useState(0);
  const [modal, setModal] = useState<null | 'amount' | 'reject' | 'bulk'>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [bulkResult, setBulkResult] = useState<BulkSummary | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [recent, setRecent] = useState<Decision[]>(() => SESSION_DECISIONS[shopId] ?? []);

  const flash = useCallback((text: string) => {
    setToast(text);
    window.setTimeout(() => setToast((t) => (t === text ? null : t)), 3000);
  }, []);

  const load = useCallback(async (quiet = false) => {
    if (!shopId) return;
    if (!quiet) setLoading(true);
    try {
      const res = await InvoiceRepository.pendingClaims(shopId);
      const list = [...(res.items || [])].sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
      setItems(list);
      setLoadError(null);
      setPicked((p) => Object.fromEntries(Object.entries(p).filter(([id, on]) => on && list.some((c) => c.paymentId === id))));
    } catch (e) {
      setLoadError(e);
      if (!quiet) setItems([]);
    } finally {
      setLoading(false);
    }
  }, [shopId]);

  useEffect(() => {
    setRecent(SESSION_DECISIONS[shopId] ?? []);
    setPicked({});
    setBulkResult(null);
    setProblem(null);
    load();
  }, [load, shopId]);

  // The detail page links here with ?claim=<paymentId>; select it, then drop the param.
  useEffect(() => {
    const id = search.get('claim');
    if (!id) return;
    setSelId(id);
    const next = new URLSearchParams(search);
    next.delete('claim');
    setSearch(next, { replace: true });
  }, [search, setSearch]);

  const sel = useMemo(() => items.find((c) => c.paymentId === selId) ?? items[0] ?? null, [items, selId]);
  useEffect(() => { setZoom(1); setRot(0); }, [sel?.paymentId]);

  const pickedList = items.filter((c) => picked[c.paymentId]);
  const allOn = items.length > 0 && pickedList.length === items.length;

  const remember = (d: Decision) => {
    const list = [d, ...(SESSION_DECISIONS[shopId] ?? []).filter((x) => x.paymentId !== d.paymentId)].slice(0, RECENT_MAX);
    SESSION_DECISIONS[shopId] = list;
    setRecent(list);
  };
  const decision = (c: PendingClaim, state: Decision['state'], amountMinor: number, why: string): Decision => ({
    paymentId: c.paymentId, docId: c.docId, number: c.document?.number ?? null, customer: custOf(c),
    amountMinor, state, why, at: Date.now(),
  });

  const afterDecision = async (text: string, d?: Decision) => {
    setModal(null);
    setProblem(null);
    if (d) remember(d);
    await load(true);
    flash(text);
  };

  const approve = async (c: PendingClaim) => {
    if (busy) return;
    setBusy('approve');
    setProblem(null);
    try {
      const res = await InvoiceRepository.approveClaim(shopId, c.paymentId);
      await afterDecision(
        `Approved · ${c.document?.number ?? 'invoice'} updated${res.receipt?.number ? ` · receipt ${res.receipt.number}` : ''}`,
        decision(c, 'Approved', c.amountMinor, METHOD_LABEL[c.method] ?? c.method),
      );
    } catch (e: any) {
      setProblem(claimErrorText(e));
      if (e?.code === 'claim_not_pending') load(true);
    } finally {
      setBusy(null);
    }
  };

  const approveBulk = async () => {
    if (busy || !pickedList.length) return;
    setBusy('bulk');
    setProblem(null);
    const sent = pickedList;
    try {
      const res = await InvoiceRepository.approveBulk(shopId, sent.map((c) => c.paymentId));
      const summary = bulkSummary(res, sent.map((c) => ({
        paymentId: c.paymentId, amountMinor: c.amountMinor, label: [c.document?.number, custOf(c)].filter(Boolean).join(' · '),
      })), cur);
      const ok = new Set((res.approved || []).map((a) => a.paymentId));
      sent.filter((c) => ok.has(c.paymentId)).forEach((c) => remember(decision(c, 'Approved', c.amountMinor, METHOD_LABEL[c.method] ?? c.method)));
      setPicked({});
      setBulkResult(summary.failed.length ? summary : null);
      await afterDecision(summary.text);
    } catch (e: any) {
      setModal(null);
      setProblem(claimErrorText(e));
    } finally {
      setBusy(null);
    }
  };

  const shell = 'h-screen flex flex-col w-full p-5 md:px-8 md:py-7 overflow-auto [&>*]:shrink-0 gap-5';

  if (!shopId) {
    return <div className={shell}><p className="text-text2">Select a shop to see its payment claims.</p></div>;
  }

  const forbidden = loadError?.status === 403 || loadError?.code === 'forbidden_role';
  const showPanel = !!sel && !loading && !loadError;

  return (
    <div className={shell}>
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="m-0 text-[28px] font-semibold tracking-[-0.015em] text-text1">Approvals</h1>
          <span className="text-[14px] text-text2">Customers said they paid. Check your bank or LankaQR app, then approve or reject.</span>
        </div>
        {pickedList.length > 0 && (
          <button type="button" className="nl-btn nl-btn--primary" disabled={!!busy} onClick={() => setModal('bulk')}>
            Approve {pickedList.length} selected
          </button>
        )}
      </header>

      {problem && (
        <ErrorBanner text={problem}>
          <div className="mt-2">
            <button type="button" className="nl-btn nl-btn--xs nl-btn--ghost" onClick={() => setProblem(null)}>Dismiss</button>
          </div>
        </ErrorBanner>
      )}

      {bulkResult && (
        <div role="alert" className="rounded-lg border border-warn/40 bg-warn-soft px-3.5 py-2.5 text-[13px]" style={{ color: 'var(--warnFg)' }}>
          <span className="font-semibold">{bulkResult.text}</span>
          <ul className="mb-0 mt-1.5 list-disc pl-5">
            {bulkResult.failed.map((f) => <li key={f.paymentId}><span className="nl-num">{f.label}</span>: {f.reason}</li>)}
          </ul>
          <div className="mt-2">
            <button type="button" className="nl-btn nl-btn--xs nl-btn--ghost" onClick={() => setBulkResult(null)}>Dismiss</button>
          </div>
        </div>
      )}

      <div className={`grid grid-cols-1 items-start gap-5 ${showPanel ? 'min-[1180px]:grid-cols-[minmax(0,1fr)_400px]' : ''}`}>
        <div className="flex min-w-0 flex-col gap-4">
          {/* Queue */}
          <div className="nl-card overflow-x-auto p-0">
            <div className="min-w-[720px]" role="table" aria-label="Payment claims waiting for approval">
              <div
                role="row"
                className={`grid items-center gap-3 bg-surface2 px-[18px] py-2.5 ${capsCls}`}
                style={{ gridTemplateColumns: GRID }}
              >
                <span role="columnheader">
                  <input
                    type="checkbox"
                    className="m-0 h-4 w-4"
                    style={{ accentColor: 'var(--brand)' }}
                    checked={allOn}
                    disabled={!items.length || !!loadError}
                    aria-label="Select all claims"
                    onChange={() => setPicked(allOn ? {} : Object.fromEntries(items.map((c) => [c.paymentId, true])))}
                  />
                </span>
                <span role="columnheader">Slip</span>
                <span role="columnheader">Customer · Invoice</span>
                <span role="columnheader" className="text-right">Amount</span>
                <span role="columnheader">Method</span>
                <span role="columnheader">Paid on</span>
                <span role="columnheader">Waiting</span>
              </div>

              {loading && [0, 1, 2, 3].map((i) => (
                <div key={i} className="grid items-center gap-3 border-t border-borderc px-[18px] py-3" style={{ gridTemplateColumns: GRID }} aria-hidden>
                  <span className="h-4 w-4 animate-pulse rounded bg-surface3" />
                  <span className="h-12 w-[38px] animate-pulse rounded-md bg-surface3" />
                  <span className="h-4 w-40 animate-pulse rounded bg-surface3" />
                  <span className="ml-auto h-4 w-20 animate-pulse rounded bg-surface3" />
                  <span className="h-4 w-16 animate-pulse rounded bg-surface3" />
                  <span className="h-4 w-16 animate-pulse rounded bg-surface3" />
                  <span className="h-4 w-12 animate-pulse rounded bg-surface3" />
                </div>
              ))}

              {!loading && loadError && (
                <div className="flex flex-col items-center gap-2 border-t border-borderc px-5 py-10 text-center">
                  <span className="font-semibold text-text1">{forbidden ? APPROVER_ONLY : 'Claims could not load'}</span>
                  {!forbidden && <span className="text-[13px] text-text2">{claimErrorText(loadError)}</span>}
                  {!forbidden && <button type="button" className="nl-btn nl-btn--sm nl-btn--secondary" onClick={() => load()}>Try again</button>}
                </div>
              )}

              {!loading && !loadError && items.length === 0 && (
                <div className="flex flex-col items-center gap-1.5 border-t border-borderc px-5 py-12 text-center">
                  <span className="font-semibold text-text1">No payments waiting for approval</span>
                  <span className="max-w-md text-[13px] text-text2">
                    When a customer taps “I&apos;ve paid” on an invoice link, the claim and slip appear here.
                  </span>
                </div>
              )}

              {!loading && !loadError && items.map((c) => {
                const on = sel?.paymentId === c.paymentId;
                const w = waiting(c.createdAt);
                return (
                  <div
                    key={c.paymentId}
                    role="row"
                    tabIndex={0}
                    aria-selected={on}
                    onClick={() => setSelId(c.paymentId)}
                    onKeyDown={(e) => {
                      if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) { e.preventDefault(); setSelId(c.paymentId); }
                    }}
                    className={`grid cursor-pointer items-center gap-3 border-t border-borderc px-[18px] py-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-brand ${on ? 'bg-brand-soft' : 'hover:bg-surface2'}`}
                    style={{ gridTemplateColumns: GRID, boxShadow: on ? 'inset 3px 0 0 var(--brand)' : undefined }}
                  >
                    <span role="cell" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="m-0 h-4 w-4"
                        style={{ accentColor: 'var(--brand)' }}
                        checked={!!picked[c.paymentId]}
                        aria-label={`Select claim for ${c.document?.number ?? 'invoice'}`}
                        onChange={() => setPicked((p) => ({ ...p, [c.paymentId]: !p[c.paymentId] }))}
                      />
                    </span>
                    <span role="cell"><SlipThumb claim={c} width={38} height={48} /></span>
                    <span role="cell" className="flex min-w-0 flex-col">
                      <span className="truncate font-semibold text-text1">{custOf(c)}</span>
                      <span className="flex flex-wrap items-center gap-2">
                        <Link
                          to={docLink(c.docId)}
                          className="font-mono text-[12px] text-text3 hover:text-brand"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {c.document?.number ?? 'Invoice'}
                        </Link>
                        {c.possibleDuplicate && <DuplicateTag small />}
                      </span>
                    </span>
                    <span role="cell" className="nl-num text-right font-semibold tabular-nums text-text1">{money(c.amountMinor, cur)}</span>
                    <span role="cell" className="text-[13px] text-text2">{METHOD_LABEL[c.method] ?? c.method}</span>
                    <span role="cell" className="text-[13px] text-text2">{date(c.paidOn)}</span>
                    <span role="cell" className="text-[13px] font-semibold" style={{ color: w.warn ? 'var(--warnFg)' : 'var(--text2)' }}>{w.text}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Recently decided (this session) */}
          {recent.length > 0 && (
            <section className="nl-card overflow-hidden p-0" aria-labelledby="ap-recent">
              <h2 id="ap-recent" className="m-0 px-[18px] py-3.5 text-[14px] font-semibold text-text1">Recently decided</h2>
              {recent.map((d) => {
                const ok = d.state === 'Approved';
                return (
                  <div key={`${d.paymentId}-${d.at}`} className="grid items-center gap-3 border-t border-borderc px-[18px] py-2.5" style={{ gridTemplateColumns: 'minmax(0,1fr) auto auto' }}>
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate font-semibold text-text1">{d.customer}</span>
                      <span className="truncate text-[12px] text-text3">
                        <Link to={docLink(d.docId)} className="font-mono text-text3 hover:text-brand">{d.number ?? 'Invoice'}</Link>
                        {' · '}{d.why}{' · '}{new Date(d.at).toTimeString().slice(0, 5)}
                      </span>
                    </span>
                    <span className="nl-num font-semibold tabular-nums text-text1">{money(d.amountMinor, cur)}</span>
                    <span
                      className={`inline-flex h-[22px] items-center rounded-full px-[9px] text-[12px] font-semibold ${ok ? 'bg-pos-soft' : 'bg-neg-soft'}`}
                      style={{ color: ok ? 'var(--posFg)' : 'var(--negFg)' }}
                    >
                      {d.state}
                    </span>
                  </div>
                );
              })}
            </section>
          )}
        </div>

        {/* Selected claim */}
        {showPanel && sel && (
          <SelectedClaim
            claim={sel}
            currency={cur}
            zoom={zoom}
            rot={rot}
            busy={busy}
            onZoom={(dz) => setZoom((z) => Math.round(Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z + dz)) * 10) / 10)}
            onRotate={() => setRot((r) => (r + 90) % 360)}
            onApprove={() => approve(sel)}
            onDifferent={() => setModal('amount')}
            onReject={() => setModal('reject')}
          />
        )}
      </div>

      {/* Dialogs */}
      {modal === 'amount' && sel && (
        <DifferentAmountDialog
          shopId={shopId}
          claim={sel}
          docNumber={sel.document?.number}
          balanceMinor={sel.document?.balanceMinor ?? 0}
          currency={cur}
          onClose={() => setModal(null)}
          onStale={() => load(true)}
          onDone={(m) => afterDecision(`Approved ${money(m, cur)} · ${sel.document?.number ?? 'invoice'} updated`, decision(sel, 'Approved', m, `${METHOD_LABEL[sel.method] ?? sel.method} · claimed ${money(sel.amountMinor, cur)}`))}
        />
      )}
      {modal === 'reject' && sel && (
        <RejectDialog
          shopId={shopId}
          claim={sel}
          docNumber={sel.document?.number}
          currency={cur}
          onClose={() => setModal(null)}
          onStale={() => load(true)}
          onDone={(why) => afterDecision('Claim rejected. The customer sees the reason on the invoice link.', decision(sel, 'Rejected', sel.amountMinor, why))}
        />
      )}
      {modal === 'bulk' && (
        <Dialog
          title={`Approve ${pickedList.length} payment${pickedList.length === 1 ? '' : 's'}`}
          sub="Each claim is approved at the amount the customer sent. Each invoice is updated and gets a receipt."
          onClose={() => setModal(null)}
          busy={busy === 'bulk'}
          footer={(
            <>
              <button type="button" className="nl-btn nl-btn--secondary" onClick={() => setModal(null)} disabled={busy === 'bulk'}>Cancel</button>
              <button type="button" className="nl-btn nl-btn--primary" onClick={approveBulk} disabled={busy === 'bulk' || !pickedList.length || pickedList.length > 50}>
                {busy === 'bulk' ? 'Approving…' : `Approve ${money(totalOf(pickedList), cur)}`}
              </button>
            </>
          )}
        >
          <ul className="m-0 flex max-h-[320px] list-none flex-col overflow-auto rounded-[10px] border border-borderc p-0">
            {pickedList.map((c) => (
              <li key={c.paymentId} className="grid items-center gap-3 border-b border-borderc px-3.5 py-2.5 last:border-b-0" style={{ gridTemplateColumns: 'minmax(0,1fr) auto' }}>
                <span className="flex min-w-0 flex-col">
                  <span className="truncate font-semibold text-text1">{custOf(c)}</span>
                  <span className="font-mono text-[12px] text-text3">{c.document?.number ?? 'Invoice'}</span>
                </span>
                <span className="nl-num font-semibold tabular-nums text-text1">{money(c.amountMinor, cur)}</span>
              </li>
            ))}
          </ul>
          <div className="mt-3 flex justify-between px-1 text-[14px] font-bold text-text1">
            <span>Total</span><span className="nl-num tabular-nums">{money(totalOf(pickedList), cur)}</span>
          </div>
          {pickedList.length > 50 && (
            <p className="mb-0 mt-2 text-[12px]" style={{ color: 'var(--negFg)' }}>Approve at most 50 at a time.</p>
          )}
        </Dialog>
      )}

      {toast && (
        <div
          role="status"
          className="fixed bottom-6 left-1/2 z-[1100] flex -translate-x-1/2 items-center gap-2 rounded-[10px] px-4 py-2.5 text-[14px] font-medium text-white"
          style={{ background: '#131318', boxShadow: 'var(--shadowLg)' }}
        >
          <span aria-hidden style={{ color: '#45D39F' }}>✓</span>{toast}
        </div>
      )}
    </div>
  );
}

function SelectedClaim({
  claim, currency, zoom, rot, busy, onZoom, onRotate, onApprove, onDifferent, onReject,
}: {
  claim: PendingClaim;
  currency: string;
  zoom: number;
  rot: number;
  busy: string | null;
  onZoom: (delta: number) => void;
  onRotate: () => void;
  onApprove: () => void;
  onDifferent: () => void;
  onReject: () => void;
}) {
  const kind = slipKind(claim);
  const before = Math.max(0, claim.document?.balanceMinor ?? 0);
  const after = balanceAfter(before, claim.amountMinor);
  const zoomBtn = 'grid h-7 w-7 place-items-center rounded-[7px] border border-borderc2 bg-surface text-text1 disabled:opacity-40';

  return (
    <aside
      className="nl-card flex flex-col overflow-hidden p-0 min-[1180px]:sticky min-[1180px]:top-0"
      aria-label="Selected payment claim"
    >
      <div className="flex items-center justify-between gap-2 border-b border-borderc px-4 py-3">
        <span className="font-semibold text-text1">Payment slip</span>
        {kind === 'image' && (
          <div className="flex items-center gap-1.5">
            <button type="button" className={zoomBtn} aria-label="Zoom out" disabled={zoom <= ZOOM_MIN} onClick={() => onZoom(-ZOOM_STEP)}>−</button>
            <span className="min-w-[40px] text-center text-[12px] font-semibold text-text1" aria-live="polite">{Math.round(zoom * 100)}%</span>
            <button type="button" className={zoomBtn} aria-label="Zoom in" disabled={zoom >= ZOOM_MAX} onClick={() => onZoom(ZOOM_STEP)}>+</button>
            <button type="button" className="h-7 rounded-[7px] border border-borderc2 bg-surface px-2.5 text-[12px] font-semibold text-text1" onClick={onRotate}>
              ↻ Rotate 90°
            </button>
          </div>
        )}
        {kind === 'pdf' && (
          <a href={claim.slipUrl} target="_blank" rel="noopener noreferrer" className="text-[13px] font-semibold text-brand">Open slip ↗</a>
        )}
      </div>

      <div className="grid h-[340px] place-items-center overflow-hidden" style={{ background: STRIPES }}>
        {kind === 'image' && (
          <img
            src={claim.slipUrl}
            alt={`Payment slip for ${claim.document?.number ?? 'the invoice'}`}
            className="max-h-[320px] max-w-[90%] rounded object-contain"
            style={{ transform: `scale(${zoom}) rotate(${rot}deg)`, transition: 'transform .2s', boxShadow: '0 4px 18px rgba(0,0,0,.18)' }}
          />
        )}
        {kind === 'pdf' && (
          <a
            href={claim.slipUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center gap-2 rounded-xl bg-surface px-6 py-5 text-center no-underline"
            style={{ boxShadow: 'var(--shadow)' }}
          >
            <span className="rounded bg-neg-soft px-2 py-1 font-mono text-[12px] font-bold" style={{ color: 'var(--negFg)' }}>PDF</span>
            <span className="text-[13px] font-semibold text-brand">Open slip</span>
          </a>
        )}
        {kind === 'none' && <span className="rounded-lg bg-surface px-3 py-2 text-[13px] text-text2">No slip attached</span>}
      </div>

      <div className="flex flex-col gap-3 p-4">
        <div className="flex items-start justify-between gap-2.5">
          <div className="flex min-w-0 flex-col">
            <span className="truncate font-semibold text-text1">{custOf(claim)}</span>
            <Link to={docLink(claim.docId)} className="self-start font-mono text-[13px] text-brand">
              {claim.document?.number ?? 'Invoice'} ↗
            </Link>
          </div>
          {claim.possibleDuplicate && <DuplicateTag />}
        </div>
        {claim.possibleDuplicate && (
          <span className="text-[12px]" style={{ color: 'var(--negFg)' }}>
            Same amount and reference as another payment on this invoice. Approve only one.
          </span>
        )}
        {claim.note && (
          <span className="break-words rounded-lg bg-surface2 px-2.5 py-2 text-[13px] text-text2">“{claim.note}”</span>
        )}
        <dl className="m-0 grid grid-cols-2 gap-x-3 gap-y-1.5 text-[13px]">
          <dt className="text-text3">Amount</dt><dd className="m-0 nl-num text-right font-semibold tabular-nums text-text1">{money(claim.amountMinor, currency)}</dd>
          <dt className="text-text3">Method</dt><dd className="m-0 text-right text-text1">{METHOD_LABEL[claim.method] ?? claim.method}</dd>
          <dt className="text-text3">Paid on</dt><dd className="m-0 text-right text-text1">{date(claim.paidOn)}</dd>
          <dt className="text-text3">Reference</dt><dd className="m-0 truncate text-right font-mono text-text1">{claim.reference || '—'}</dd>
          {claim.payerName && (<><dt className="text-text3">Paid by</dt><dd className="m-0 truncate text-right text-text1">{claim.payerName}</dd></>)}
        </dl>

        <div className="grid items-center gap-2.5 rounded-[10px] bg-surface2 p-3 tabular-nums" style={{ gridTemplateColumns: '1fr auto 1fr' }}>
          <div className="flex flex-col">
            <span className="text-[12px] text-text3">Balance before</span>
            <span className="nl-num font-semibold text-text1">{money(before, currency)}</span>
          </div>
          <span aria-hidden className="text-text3">→</span>
          <div className="flex flex-col">
            <span className="text-[12px] text-text3">After approval</span>
            <span className="nl-num font-bold" style={{ color: after === 0 ? 'var(--posFg)' : 'var(--text)' }}>{money(after, currency)}</span>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <button type="button" className="nl-btn nl-btn--primary h-[42px]" disabled={!!busy} onClick={onApprove}>
            {busy === 'approve' ? 'Approving…' : `Approve ${money(claim.amountMinor, currency)}`}
          </button>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" className="nl-btn nl-btn--secondary" disabled={!!busy} onClick={onDifferent}>Different amount</button>
            <button type="button" className="nl-btn nl-btn--secondary" style={{ color: 'var(--negFg)' }} disabled={!!busy} onClick={onReject}>Reject</button>
          </div>
        </div>
      </div>
    </aside>
  );
}
